import { useNode, useEditor } from "@craftjs/core";
import { getRandomId as getRandomNodeId, ROOT_NODE } from "@craftjs/utils";
import React, {
  useEffect,
  useRef,
  useCallback,
  useContext,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  faGrip,
  faArrowUp,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GrigoraDashboardContext } from "../../dashboard/context";

import "./_RenderNode.scss";
import { GrigoraEditorContext } from "../context";
import {
  Component,
  DeleteIcon,
  DragIcon,
  SkipUpIcon,
} from "../../../assets/ui-icons";
import { Tooltip } from "../../input";

const RenderNode = ({ render }) => {
  const { editorDocument, editorWindow } = useContext(GrigoraDashboardContext);

  const { blockState, setBlockState } = useContext(GrigoraDashboardContext);

  const { setDisplaySize } = useContext(GrigoraEditorContext);

  const { id } = useNode();

  const {
    actions,
    selected,
    isFirst,
    query,
    isActive,
    selectedSize,
    isBeingDraggedOver,
  } = useEditor((_, query) => {
    let isBeingDraggedOver = false;

    for (const nodeId of query?.getDraggedOverNodes?.() ?? []) {
      // we are looking for the first canvas element
      if (query.node(nodeId).isCanvas()) {
        // if the id of first canvas element is the same as this Container's id we know that the user is dragging over this Container
        if (nodeId === id) {
          isBeingDraggedOver = true;
        }
        // Since we are only interested in the first Canvas, we break out of the loop
        break;
      }
    }

    return {
      isActive: query.getEvent("selected").contains(id),
      selected: query.getEvent("selected"),
      isFirst: query.getEvent("selected").first() === id,
      selectedSize: query.getEvent("selected").size(),
      isBeingDraggedOver: isBeingDraggedOver,
    };
  });

  const {
    isHover,
    dom,
    name,
    slug,
    moveable,
    deletable,
    connectors: { drag },
    parent,
    props,
  } = useNode((node) => ({
    isHover: node.events.hovered,
    dom: node.dom,
    name: node.data.custom.displayName || node.data.displayName,
    slug: node.data.custom.slug ? node.data.custom.slug : "",
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent,
    props: node.data.props,
  }));

  const [toolbarMoreOptionsVisible, setToolbarMoreOptionsVisible] =
    useState(false);

  const [isEditingName, setIsEditingName] = useState(false);
  const [internalBlockName, setInternalBlockName] = useState(name);

  const activeRef = useRef();
  // const hoverRef = useRef();

  useEffect(() => {
    if (dom) {
      if (isActive && slug !== "grigora/page") {
        if (slug === "grigora/portal") {
          if (!blockState(id)?.["opened"]) {
            dom.classList.add("grigora-block-selected");
          } else {
            dom.classList.remove("grigora-block-selected");
            dom.classList.remove("grigora-block-hover");
          }
        } else if (slug === "grigora/content-locker-popup") {
          if (!blockState(id)?.["is-opened"]) {
            dom.classList.add("grigora-block-selected");
          } else {
            dom.classList.remove("grigora-block-selected");
            dom.classList.remove("grigora-block-hover");
          }
        } else {
          dom.classList.add("grigora-block-selected");
        }
        if (selectedSize > 1) {
          dom.classList.add("grigora-block-group");
        }
      } else if (isHover && slug !== "grigora/page")
        dom.classList.add("grigora-block-hover");
      else {
        dom.classList.remove("grigora-block-selected");
        dom.classList.remove("grigora-block-hover");
      }

      if (props.isComponent) {
        dom.classList.add("grigora-block-component");
      } else {
        dom.classList.remove("grigora-block-component");
      }

      if (isBeingDraggedOver) {
        dom.classList.add("grigora-block-dragged-over");
      } else {
        dom.classList.remove("grigora-block-dragged-over");
      }
    }
  }, [dom, isActive, isHover, isBeingDraggedOver]);

  const getPos = useCallback((dom) => {
    const topOffsetY = 70;
    const toolbarHeight = 30;

    const { top, left, bottom } = dom
      ? dom.getBoundingClientRect()
      : { top: 0, left: 0, bottom: 0 };

    // Check if element is in iframe and get offset of iframe
    const frameOffset =
      dom?.ownerDocument?.defaultView?.frameElement?.getBoundingClientRect() ??
      {};

    const {
      top: frameTop,
      bottom: frameBottom,
      left: frameLeft,
    } = frameOffset ? frameOffset : { frameTop: 0, frameBottom: 0 };

    const isElementBottomInsideScreen = bottom - frameBottom < 0;

    const isThereSpaceBelowForToolbar =
      bottom + topOffsetY + toolbarHeight < frameBottom;

    const isOutside =
      (bottom < 0 && top < 0) ||
      (bottom - frameBottom > 0 && top - frameBottom > 0);

    if (isOutside && toolbarMoreOptionsVisible) {
      setToolbarMoreOptionsVisible(false);
    }

    return {
      display: isOutside ? "none" : "flex",
      top:
        top - topOffsetY < 0
          ? frameTop +
            topOffsetY +
            (isElementBottomInsideScreen
              ? isThereSpaceBelowForToolbar
                ? bottom
                : bottom - topOffsetY
              : 0)
          : top + frameTop,
      left: left + frameLeft,
    };
  }, []);

  const scroll = () => {
    const { current: activeDOM } = activeRef;

    if (activeDOM) {
      const { display, top, left } = getPos(dom);
      activeDOM.style.display = display;
      activeDOM.style.top = `${top}px`;
      activeDOM.style.left = `${left}px`;
    }
  };

  // function throttle(timer) {
  //   let queuedCallback;
  //   return (callback) => {
  //     if (!queuedCallback) {
  //       timer(() => {
  //         const cb = queuedCallback;
  //         queuedCallback = null;
  //         cb();
  //       });
  //     }
  //     queuedCallback = callback;
  //   };
  // }

  // const throttledWrite = throttle(requestAnimationFrame);

  // const eventHandler = useCallback(() => {
  //   throttledWrite(scroll);
  // }, [scroll]);

  useEffect(() => {
    // if(!editorDocument){
    //   return;
    // }

    if (isActive) {
      scroll();
      editorDocument
        ?.querySelector(".grigora-editor-renderer-canvas")
        ?.addEventListener("scroll", scroll, { passive: true });
      editorWindow?.addEventListener("resize", scroll);
    }

    return () => {
      // if(isActive){
      editorDocument
        ?.querySelector(".grigora-editor-renderer-canvas")
        ?.removeEventListener("scroll", scroll, { passive: true });
      editorWindow?.removeEventListener("resize", scroll);
      // }
    };
  }, [dom, isActive]);

  function shouldShowToolbar() {
    if (slug === "grigora/content-locker-popup") {
      if (blockState(id)?.["is-opened"]) {
        return false;
      }
    }

    return (
      (((isActive && slug !== "grigora/page" && selectedSize === 1) ||
        (selectedSize > 1 && isFirst && isActive && slug !== "grigora/page")) &&
        (slug !== "grigora/portal" ||
          (slug === "grigora/portal" && !blockState(id)?.["opened"]))) ||
      (isHover && slug !== "grigora/page")
    );
  }

  return (
    <>
      {ReactDOM.createPortal(
        <>
          {shouldShowToolbar() && (
            <div
              ref={activeRef}
              className={`g-editor-toolbar absolute top-0 left-0 block-${id} flex gap-1 text-black rounded-[5px] bg-white fixed items-center`}
              style={{
                zIndex: 98,
              }}
            >
              <div className="g-editor-toolbar__inner flex items-center">
                {props.isComponent ? (
                  <>
                    <Component className="w-[15px] h-[15px] mr-2" />
                  </>
                ) : (
                  <></>
                )}
                {selectedSize > 1 ? (
                  "Group"
                ) : (
                  <input
                    className="g-editor-toolbar__input outline-none w-[90px] text-ellipsis mr-2"
                    style={{
                      cursor: isEditingName ? "text" : "default",
                      outline: isEditingName ? "1px solid #000" : "none",
                    }}
                    readOnly={!isEditingName}
                    onDoubleClick={() => {
                      if (!isEditingName) {
                        setInternalBlockName(name);
                        setIsEditingName(true);
                      }
                    }}
                    value={isEditingName ? internalBlockName : name}
                    onChange={(e) => {
                      setInternalBlockName(e.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        actions.setCustom(
                          id,
                          (custom) => (custom.displayName = internalBlockName),
                        );
                        setIsEditingName(false);
                      }
                    }}
                    onBlur={(e) => {
                      actions.setCustom(
                        id,
                        (custom) => (custom.displayName = internalBlockName),
                      );
                      setIsEditingName(false);
                    }}
                  />
                )}
                {moveable && selectedSize === 1 ? (
                  <Tooltip text="Drag" placement="top" mouseEnterDelay={2}>
                    <a
                      className="g-editor-toolbar__button p-1 cursor-grab active:cursor-grabbing"
                      ref={drag}
                    >
                      <DragIcon className="drag-icon w-[15px] h-[15px] text-black fill-black" />
                    </a>
                  </Tooltip>
                ) : null}
                {id !== ROOT_NODE && (
                  <Tooltip
                    text="Select Parent"
                    placement="top"
                    mouseEnterDelay={2}
                  >
                    <a
                      className="g-editor-toolbar__button p-1 cursor-pointer"
                      onClick={() => {
                        actions.selectNode(parent);
                      }}
                    >
                      <SkipUpIcon className="skip-up-icon w-[15px] h-[15px] text-black fill-black" />
                    </a>
                  </Tooltip>
                )}
                {id !== ROOT_NODE && deletable ? (
                  <Tooltip text="Delete" placement="top" mouseEnterDelay={2}>
                    <a
                      className="g-editor-toolbar__button p-1 cursor-grab active:cursor-grabbing"
                      onClick={() => {
                        actions.delete(id);
                      }}
                    >
                      <DeleteIcon className="delete-icon w-[15px] h-[15px] text-black fill-black" />
                    </a>
                  </Tooltip>
                ) : null}
                <div className="g-editor-toolbar__portal"></div>
              </div>
            </div>
          )}
          {/* {((isHover && !isActive) || isBeingDraggedOver) && slug !== "grigora/page" && (
            <div
              ref={hoverRef}
              className={`g-editor-toolbar-hover px-2 py-0 text-blue-600 fixed items-center`}
              style={{
                display: display,
                left: `${left + sideBarWidth}px`,
                top: `${top}px`,
                zIndex: 9998,
              }}
            >
              <h2 className="flex-1 mr-4">{name}</h2>
            </div>
          )} */}
        </>,
        document.body,
      )}
      {render}
    </>
  );
};

export default RenderNode;
